import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { NODE_TYPE_REGISTRY } from '../handlers/canvas-helpers';
import { MINDMAP_NODE_TYPES, type MindmapNodeType } from '../types';
import * as styles from './type-picker-popover.css';

export interface TypePickerAnchor {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TypePickerPopoverProps {
  anchor: TypePickerAnchor;
  onPick: (nodeType: MindmapNodeType) => void;
  onDismiss: () => void;
}

/**
 * Compact 5-chip picker that lands next to the user's text selection.
 *
 * Visual model:
 *  - Every chip uses the same neutral pill shape; type identity lives
 *    in a small left-side color dot (not in a different border per
 *    chip). This keeps the row calm and aligns with Curiescious's
 *    navy-first palette.
 *  - The default chip (`Finding`) is filled with the brand navy so
 *    the user can scan and immediately see what `Enter` does.
 *  - Keyboard shortcuts (F/H/Q/M/U) are rendered as quiet kbd-style
 *    glyphs; Esc cancels; Enter accepts the default.
 *
 * Rendered into `document.body` via a portal so the popover floats
 * above the BlockSuite editor + workbench chrome without z-index
 * gymnastics.
 */
export const TypePickerPopover = ({
  anchor,
  onPick,
  onDismiss,
}: TypePickerPopoverProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const top = anchor.y + anchor.height + 8;
  const left = Math.min(
    anchor.x,
    Math.max(8, (typeof window !== 'undefined' ? window.innerWidth : 1024) - 320)
  );

  const handlePick = useCallback(
    (nodeType: MindmapNodeType) => {
      onPick(nodeType);
    },
    [onPick]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss();
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        handlePick('finding');
        return;
      }
      for (const type of MINDMAP_NODE_TYPES) {
        if (NODE_TYPE_REGISTRY[type].shortcut.toLowerCase() === key) {
          e.preventDefault();
          handlePick(type);
          return;
        }
      }
    };

    const onPointerDown = (e: MouseEvent) => {
      const node = containerRef.current;
      if (node && !node.contains(e.target as Node)) {
        onDismiss();
      }
    };

    window.addEventListener('keydown', onKey, true);
    window.addEventListener('mousedown', onPointerDown, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('mousedown', onPointerDown, true);
    };
  }, [handlePick, onDismiss]);

  return createPortal(
    <div
      ref={containerRef}
      role="dialog"
      aria-label="Save to canvas as"
      className={styles.popover}
      style={{ top: `${top}px`, left: `${left}px` }}
    >
      <div className={styles.title}>Save to canvas as</div>
      <div className={styles.row}>
        {MINDMAP_NODE_TYPES.map(type => {
          const meta = NODE_TYPE_REGISTRY[type];
          const isDefault = type === 'finding';
          return (
            <button
              key={type}
              type="button"
              className={styles.chip}
              onClick={() => handlePick(type)}
              title={meta.description}
              data-default={isDefault ? 'true' : undefined}
            >
              <span
                className={styles.chipDot}
                style={{ backgroundColor: meta.strokeColor }}
                aria-hidden="true"
              />
              <span className={styles.chipLabel}>{meta.label}</span>
              <span className={styles.chipShortcut}>{meta.shortcut}</span>
            </button>
          );
        })}
      </div>
      <div className={styles.hint}>
        <span className={styles.hintKey}>↵</span>
        <span>{NODE_TYPE_REGISTRY.finding.label}</span>
        <span>·</span>
        <span className={styles.hintKey}>Esc</span>
        <span>to cancel</span>
      </div>
    </div>,
    document.body
  );
};
